'use client';

import {
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { Box } from '../box/box';
import { usePathname } from 'next/navigation';
import { Dispatch } from 'react';
import React from 'react';
import { navLinkActive, navLinkStyle } from './page-nav.css';
import { Flex } from '../flex/flex';
import clsx from 'clsx';

const navHeight = 64;
const activeItemOffset = 16;

export interface PageNavProps {
  children: ReactNode;
  items: string[];
}

type NavContextValue = {
  items: string[];
  isLocked?: number | boolean;
  setIsLocked: React.Dispatch<React.SetStateAction<number | undefined>>;
  active: string;
  setActive: Dispatch<SetStateAction<string>>;
};

export const escapeId = (str?: string | null) =>
  (str ? str : Math.random().toString()).replace(/[^a-z0-9]/gi, '-');

export const NavContext = createContext<NavContextValue>({
  items: [] as string[],
  setIsLocked: () => undefined,
  active: '',
  setActive: () => undefined,
});

export function PageNav({ children, items }: PageNavProps) {
  const [isLocked, setIsLocked] = useState<number>();
  const [ids] = useState(items.map((item) => escapeId(item)));
  const [active, setActive] = useState(ids[0]);

  useScrollPosition(ids, isLocked, setIsLocked, active, setActive);
  return (
    <NavContext.Provider value={{ items, isLocked, setIsLocked, active, setActive }}>
      {children}
    </NavContext.Provider>
  );
}

export const usePageNavContext = () => useContext(NavContext);

export const useScrollPosition = (
  ids: string[],
  isLocked: number | undefined,
  setIsLocked: React.Dispatch<React.SetStateAction<number | undefined>>,
  active: string,
  setActive: Dispatch<SetStateAction<string>>,
) => {
  const [positions, setPositions] = useState<{ top: number; id: string }[]>([]);
  const pathname = usePathname();
  const hash = pathname?.slice(1);
  const [initialHash] = useState(hash);

  const shouldUseScrollPosition = /^\/locations\/[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/.test(
    pathname,
  );

  useLayoutEffect(() => {
    const onResize = () => {
      setPositions(
        ids
          .map((id) => {
            const e = document.getElementById(id);

            return { top: e?.offsetTop ?? -1, id };
          })
          .sort((a, b) => b.top - a.top),
      );
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ids]);

  useLayoutEffect(() => {
    // scroll the page vertically on page load
    if (!initialHash) return;
    const el = document.getElementById(initialHash);
    if (!el) return;
    const to = el.offsetTop - navHeight - activeItemOffset;
    setIsLocked(to);
    window.scrollTo({ top: to, behavior: 'smooth' });
  }, [initialHash, setIsLocked]);

  useLayoutEffect(() => {
    const onScroll = () => {
      if (!isLocked && shouldUseScrollPosition) {
        const above = window.scrollY + window.innerHeight / 2;
        const activeHash = positions.find((pos) => pos.top < above);

        setActive(activeHash?.id ?? ids[0]);
      }
      if (
        window.scrollY === isLocked || // at the target element
        window.scrollY === 0 || // top of the page
        window.scrollY + window.innerHeight >= document.body.offsetHeight // bottom of the page
      ) {
        setIsLocked(undefined);
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [
    positions,
    isLocked,
    setIsLocked,
    hash,
    shouldUseScrollPosition,
    pathname,
    setActive,
    active,
    ids,
  ]);
};

export const NavItem = ({ label }: { label: string }) => {
  const id = escapeId(label);
  const matchingId = id;

  const { setIsLocked, active, setActive } = usePageNavContext();
  const ref = useRef<HTMLLIElement>(null);
  useLayoutEffect(() => {
    const isActive = active === matchingId;
    if (isActive) {
      const left = (ref.current?.offsetLeft ?? 0) - (ref.current?.parentElement?.offsetLeft ?? 0);
      // scroll the nav container horizontally
      ref.current
        ?.closest('[data-radix-scroll-area-viewport]')
        ?.scrollTo({ left, behavior: 'smooth' });
    }
  }, [active, matchingId, ref]);

  const navLinkAct = active === matchingId ? navLinkActive : '';

  return (
    <Box as="li" ref={ref}>
      <span
        className={clsx(navLinkStyle, navLinkAct)}
        onClick={() => {
          const el = document.getElementById(id);
          const to = el ? el.offsetTop - navHeight - activeItemOffset : 0;
          setIsLocked(to);
          window.scrollTo({ top: to, behavior: 'smooth' });
          setActive(matchingId);
        }}
      >
        {label}
      </span>
    </Box>
  );
};

export const NavBox = ({ children }: { children: ReactNode }) => {
  return (
    <ScrollArea.Root style={{ overflow: 'hidden' }}>
      <ScrollArea.Scrollbar orientation="horizontal" />
      <ScrollArea.Viewport style={{ padding: '0 12px' }}>
        <Flex
          style={{
            listStyleType: 'none',
            alignItems: 'center',
            width: 0,
            height: navHeight,
          }}
        >
          {children}
        </Flex>
      </ScrollArea.Viewport>
    </ScrollArea.Root>
  );
};

export default PageNav;
